/** Excel / CSV import and export: models, file downloads, import jobs (upload → test → run). */
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';

export type DataModel = Schemas['DataModel'];
export type DataColumn = Schemas['DataColumn'];
export type ImportJob = Schemas['ImportJob'];
export type ImportUpload = Schemas['ImportUpload'];
export type ImportOptions = NonNullable<Schemas['ImportOptions']>;
export type MissingRef = ImportOptions['onMissingRef'][string];
export type ImportMode = ImportOptions['mode'];
export type FileFormat = 'xlsx' | 'csv';

export const dataKeys = {
  models: ['data', 'models'] as const,
  jobs: ['data', 'jobs'] as const,
  job: (id: string) => ['data', 'jobs', id] as const,
};

/** Every model this user may import or export (the API leaves out the others). */
export function useDataModels() {
  const { can } = useAuth();
  const allowed = can('data.import') || can('data.export');
  return useQuery({
    queryKey: dataKeys.models,
    queryFn: () => unwrap(api.GET('/data/models')),
    select: (r) => r.data,
    enabled: allowed,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useDataModel(name: string | undefined) {
  const models = useDataModels();
  return { ...models, model: models.data?.find((m) => m.name === name) };
}

const RUNNING: ImportJob['status'][] = ['TESTING', 'RUNNING'];
export const isBusy = (job: Pick<ImportJob, 'status'> | undefined) =>
  !!job && RUNNING.includes(job.status);

/** One import job; polls while it is testing or running. */
export function useImportJob(id: string | null) {
  return useQuery({
    queryKey: dataKeys.job(id ?? ''),
    queryFn: () =>
      unwrap(api.GET('/data/import/jobs/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: !!id,
    refetchInterval: (q) => (isBusy(q.state.data) ? 1_000 : false),
  });
}

export function useImportJobs(enabled = true) {
  return useQuery({
    queryKey: dataKeys.jobs,
    queryFn: () => unwrap(api.GET('/data/import/jobs')),
    select: (r) => r.data,
    enabled,
  });
}

const CONTENT_TYPES: Record<string, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
};

/** Uploads a file as a new import job; the answer carries the suggested mapping and sample rows. */
export function uploadImport(model: string, file: File, sheet?: string) {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return unwrap(
    api.POST('/data/import/{model}', {
      params: { path: { model }, query: { fileName: file.name, sheet } },
      // The API reads the raw file bytes.
      body: file as unknown as string,
      bodySerializer: (b: unknown) => b as BodyInit,
      headers: { 'Content-Type': CONTENT_TYPES[ext] ?? 'application/octet-stream' },
    }),
  );
}

export function startImport(
  id: string,
  dryRun: boolean,
  body: { mapping: (string | null)[]; options: ImportOptions },
) {
  const path = { path: { id } };
  return dryRun
    ? unwrap(api.POST('/data/import/jobs/{id}/test', { params: path, body }))
    : unwrap(api.POST('/data/import/jobs/{id}/run', { params: path, body }));
}

// --- downloads -------------------------------------------------------------------------

function fileNameOf(response: Response, fallback: string): string {
  const header = response.headers.get('content-disposition') ?? '';
  const name = /filename="?([^";]+)"?/i.exec(header)?.[1]?.split(/[\\/]/).pop()?.trim();
  return name ?? fallback;
}

function save(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function download(
  call: Promise<{ data?: unknown; error?: unknown; response: Response }>,
  fallback: string,
) {
  const { data, response } = await call;
  if (!response.ok) throw await ApiError.from(response);
  save(data as Blob, fileNameOf(response, fallback));
}

/** Saves an export of `model`: chosen column paths (default all), chosen records (default all). */
export function downloadExport(
  model: string,
  opts: { fields?: string[]; ids?: string[]; format: FileFormat },
) {
  const query = {
    format: opts.format,
    fields: opts.fields?.length ? opts.fields.join(',') : undefined,
    ids: opts.ids?.length ? opts.ids.join(',') : undefined,
  };
  return download(
    api.GET('/data/export/{model}', {
      params: { path: { model }, query },
      parseAs: 'blob',
    }),
    `${model}.${opts.format}`,
  );
}

/** An empty file with the import columns, a sample row and the field notes. */
export function downloadTemplate(model: string, format: FileFormat) {
  return download(
    api.GET('/data/models/{model}/template', {
      params: { path: { model }, query: { format } },
      parseAs: 'blob',
    }),
    `${model}-template.${format}`,
  );
}

/** The rows that failed, with an error column, ready to fix and import again. */
export function downloadErrors(id: string, format: FileFormat) {
  return download(
    api.GET('/data/import/jobs/{id}/errors', {
      params: { path: { id }, query: { format } },
      parseAs: 'blob',
    }),
    `import-errors.${format}`,
  );
}
